import * as React from 'react';
import { Observable } from 'rxjs';
import { USER_TYPING_DEBOUNCE_TIME } from '@helpcenter/constants';
import { debounce } from 'lodash';
import type { WrappedComponentProps } from 'react-intl-next';
import { injectIntl } from 'react-intl-next';

import { searchLabel } from 'rest/label';
import type { LabelPickerProps, LabelPickerOption } from './label-picker-dumb';
import { LabelPickerDumb } from './label-picker-dumb';

import messages from './messages';

interface Props extends LabelPickerProps {
    portalId: number;
    requestTypeId: number;
    customFieldId: string | undefined;
}

type FinalProps = Omit<Props, 'loadOptions'>;

interface State {
    validationErrorMessage: string | undefined;
}

class LabelPicker extends React.PureComponent<FinalProps & WrappedComponentProps> {
    state: State = {
        validationErrorMessage: undefined,
    };

    isValidQuery = (inputValue: string): boolean => !/\s+/.test(inputValue);

    fetchLabels = (inputValue: string, callback: (options: LabelPickerOption) => void) => {
        const { intl } = this.props;
        if (this.isValidQuery(inputValue)) {
            const { portalId, requestTypeId, customFieldId } = this.props;
            return searchLabel({
                portalId,
                requestTypeId,
                customFieldId,
                query: inputValue,
            })
                .map((response) => {
                    this.setState({ validationErrorMessage: undefined });

                    return response.map((label) => ({
                        label,
                        value: label,
                    }));
                })
                .catch((errors) => {
                    // TypeScript upgrade (v4.4.3). Please correct when you revisit this code.
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    if (errors.status === 400) {
                        // Suppressing existing violation. Please fix this.
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                        this.setState({ validationErrorMessage: errors.response.errors[0].errorMessage });
                        return Observable.empty();
                    }
                    return Observable.throw(errors);
                })
                .toPromise()
                .then(callback);
        }
        this.setState({
            validationErrorMessage: intl.formatMessage(messages.inputValidationErrorMessage, {
                labelInput: inputValue,
            }),
        });
        return Observable.empty();
    };

    fetchLabelDebounced = debounce(this.fetchLabels, USER_TYPING_DEBOUNCE_TIME);

    loadOptions = (input: string, callback: (options: LabelPickerOption) => void) => {
        // Disabling existing violations, should be fixed when revisited.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.fetchLabelDebounced(input, callback);
    };

    render() {
        const { portalId, requestTypeId, ...props } = this.props;
        return (
            <LabelPickerDumb
                {...props}
                loadOptions={this.loadOptions}
                validationErrorMessage={this.state.validationErrorMessage}
            />
        );
    }
}

export default injectIntl(LabelPicker);
